import React, { useState } from 'react';

import { RefreshRightIcon } from '@vlabs/icons';
import { Control, useModal } from '@vlabs/uikit';
import type { TFileDropzoneFile } from '@vlabs/uikit/components/controls/file-dropzone/FileDropzone';
import { useTranslation } from 'react-i18next';

import { DetectionPicker } from './detection-picker/DetectionPicker';
import { handleLoadFile } from './handleLoadFile';
import { checkFacesOnLowSimilarity } from '../../entities/faces';
import { detectFaces, extractAttributes } from '@vlabs/api/luna-api/client';
import type { SampleId } from '@vlabs/api/luna-api/model';

interface SampleIdExtractorProps {
  onExtractSampleId: (sampleId: SampleId) => void;
  faceId: string;
}

export function SampleIdExtractor({ onExtractSampleId, faceId }: SampleIdExtractorProps): JSX.Element {
  const { t } = useTranslation();
  const [imageFile, setImageFile] = useState<TFileDropzoneFile>();

  const pickFaceModal = useModal({
    onRequestClose: (ctx) => ctx.close(),
  });

  const onSelectAttributeId = async (sampleId: SampleId) => {
    const extractedAttribute = await extractAttributes([sampleId], { extract_basic_attributes: 1 });
    if (!extractedAttribute[0]?.attribute_id) return;
    try {
      await checkFacesOnLowSimilarity(
        {
          reference: { type: 'face', id: faceId },
          candidates: [{
            filters: {
              origin: 'attributes',
              attribute_ids: [extractedAttribute[0].attribute_id],
            },
          }],
          context: 'changeFacePhoto',
        },
      );
    } catch {
      return;
    }
    onExtractSampleId(sampleId);
  };

  const modal = pickFaceModal.wrap(
    <DetectionPicker
      imageFile={imageFile}
      onCancel={() => {
        pickFaceModal.close();
      }}
      onSubmit={({ sampleId }) => {
        pickFaceModal.close();
        onSelectAttributeId(sampleId);
      }}
    />,
  );

  const handleClick = async () => {
    const fileFromInput = await handleLoadFile();
    if (!fileFromInput?.raw) return;

    const detectedFaces = await detectFaces(fileFromInput.raw, undefined, {
      headers: { 'Content-Type': (fileFromInput.raw).type },
    });
    const samples = detectedFaces.images[0]?.detections?.samples ?? [];

    if (samples.length > 1) {
      setImageFile(fileFromInput);
      pickFaceModal.open();
    } else {
      const sampleId = samples[0].face.sample_id as SampleId;
      onSelectAttributeId(sampleId);
    }
  };

  return (
    <React.Fragment>
      {modal}
      <Control.Button
        icon={<RefreshRightIcon />}
        onClick={handleClick}
        size="small"
        variant="flat"
      >
        {t('features:changeFacePhoto.sample_id_extractor.button.label')}
      </Control.Button>
    </React.Fragment>
  );
}
